import { useMemo, useState } from 'react'
import CLUB from '../../config/club.js'
import { SAMPLE_SEASON } from './sampleSeason.js'
import { DEFAULT_POINTS, seasonStandings, seasonCsv } from './ggSeason.js'

// G-Season — season-long by-place points & standings across events, from the
// in-code SAMPLE_SEASON. Reuses buildReports/rankTeams per event. READ-ONLY.
function download(name, text) {
  const blob = new Blob([text], { type: 'text/csv' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = name
  a.click()
}

export default function GSeason() {
  const [scheme, setScheme] = useState(DEFAULT_POINTS.slice(0, 6))
  const [dropWorst, setDropWorst] = useState(0)
  const [net, setNet] = useState(false)
  const [participation, setParticipation] = useState(0)

  const standings = useMemo(
    () => seasonStandings(SAMPLE_SEASON, { scheme, dropWorst: Number(dropWorst) || 0, net, participation: Number(participation) || 0 }),
    [scheme, dropWorst, net, participation],
  )
  const events = standings.events

  const setPts = (i, v) => setScheme((s) => s.map((x, k) => (k === i ? Number(v) : x)))
  const addPlace = () => setScheme((s) => [...s, 0])
  const removePlace = () => setScheme((s) => (s.length > 1 ? s.slice(0, -1) : s))

  return (
    <div className="card">
      <style>{PRINT_CSS}</style>

      {/* Points scheme + options */}
      <div className="g-screen space-y-3 mb-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm font-bold" style={{ color: 'var(--ge-primary)' }}>Points by finish place</span>
          <div className="flex gap-2">
            <button className="btn-ghost text-xs" onClick={() => download('season.csv', seasonCsv(standings))}>⬇ CSV</button>
            <button className="btn-primary" onClick={() => window.print()}>🖨 Print</button>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {scheme.map((pts, i) => (
            <label key={i} className="text-xs text-center">
              <span className="block text-gray-500">{i + 1}{['st', 'nd', 'rd'][i] || 'th'}</span>
              <input type="number" className="w-14 border rounded px-1 py-0.5 text-center" value={pts} onChange={(e) => setPts(i, e.target.value)} />
            </label>
          ))}
          <button className="btn-ghost text-xs" onClick={addPlace}>+ place</button>
          <button className="btn-ghost text-xs" onClick={removePlace}>− place</button>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-xs pt-2" style={{ borderTop: '1px solid #eee' }}>
          <label className="flex items-center gap-1"><input type="checkbox" checked={net} onChange={(e) => setNet(e.target.checked)} />Net standings</label>
          <label className="flex items-center gap-1"><span className="text-gray-500">Drop worst</span>
            <input type="number" min="0" className="w-12 border rounded px-1 py-0.5" value={dropWorst} onChange={(e) => setDropWorst(e.target.value)} /></label>
          <label className="flex items-center gap-1"><span className="text-gray-500">Participation pts</span>
            <input type="number" min="0" className="w-12 border rounded px-1 py-0.5" value={participation} onChange={(e) => setParticipation(e.target.value)} /></label>
        </div>
      </div>

      {/* Season standings */}
      <div className="g-print">
        <div className="g-rpt-head">
          <img src={CLUB.logo} alt={CLUB.name} className="g-rpt-logo" />
          <div>
            <div className="g-rpt-club">{CLUB.name}</div>
            <div className="g-rpt-event">{SAMPLE_SEASON.name} · Season Standings · {net ? 'Net' : 'Gross'}{Number(dropWorst) > 0 ? ` · drop worst ${dropWorst}` : ''}</div>
          </div>
        </div>
        <table className="g-tbl">
          <thead>
            <tr>
              <th>Rank</th><th>Team</th>
              {events.map((e) => <th key={e.id} className="g-num" title={e.name}>{e.name}</th>)}
              <th className="g-num">Total</th>
            </tr>
          </thead>
          <tbody>
            {standings.rows.map((r, i) => (
              <tr key={r.teamId}>
                <td>{i + 1}</td>
                <td>{r.teamName}</td>
                {r.perEvent.map((pe) => (
                  <td key={pe.eventId} className="g-num" style={pe.dropped ? { color: '#bbb', textDecoration: 'line-through' } : undefined}
                      title={pe.place ? `${pe.place}${['st', 'nd', 'rd'][pe.place - 1] || 'th'} place` : ''}>
                    {pe.points}
                  </td>
                ))}
                <td className="g-num"><b>{r.total}</b></td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="g-muted" style={{ marginTop: 8, fontSize: 11 }}>
          Points awarded by finish place each event{Number(dropWorst) > 0 ? `; each team's ${dropWorst} worst event(s) dropped (struck through)` : ''}. Ties split the averaged points.
        </p>
      </div>
    </div>
  )
}

const PRINT_CSS = `
  .g-rpt-head { display: flex; align-items: center; gap: 14px; padding-bottom: 10px; border-bottom: 3px solid ${CLUB.accentColor}; margin-bottom: 14px; }
  .g-rpt-logo { height: 52px; object-fit: contain; }
  .g-rpt-club { font-weight: 800; font-size: 18px; color: ${CLUB.primaryColor}; }
  .g-rpt-event { font-size: 12px; color: #555; }
  .g-tbl { width: 100%; border-collapse: collapse; font-size: 12px; }
  .g-tbl th, .g-tbl td { border: 1px solid #ddd; padding: 4px 6px; text-align: left; }
  .g-tbl th { background: #f6f6f2; color: #555; }
  .g-tbl .g-num { text-align: right; }
  .g-muted { color: #999; }
  @media print {
    body * { visibility: hidden !important; }
    .g-print, .g-print * { visibility: visible !important; }
    .g-print { position: absolute; left: 0; top: 0; width: 100%; }
    .g-screen { display: none !important; }
    @page { size: portrait; margin: 0.4in; }
  }
`
